/**
 * PledgesManagement - Manage campaign pledges with partial payment support.
 * Iter 414: multi-currency, fulfilment dashboard, CSV import, payment reversal.
 */

import React, { useState, useRef } from 'react';
import { PersonPicker } from '@/components/people/PersonPicker';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import {
  Plus, DollarSign, Users, Clock, CheckCircle2, AlertCircle,
  CreditCard, Loader2, Trash2, Edit2, ChevronDown, ChevronUp,
  Calendar, Phone, Mail, Receipt, Upload, Undo2, LayoutDashboard, List
} from 'lucide-react';
import PledgesDashboard from './PledgesDashboard';
import CurrencySelect from './CurrencySelect';

// Common currency codes surfaced first in dropdowns. The full catalogue is
// fetched lazily via /fx/currencies so admins can pick any supported code.
const QUICK_CURRENCIES = ['USD', 'EUR', 'GBP', 'ZAR', 'ZIG', 'NGN', 'KES', 'GHS', 'INR', 'AUD', 'CAD'];

const PledgesManagement = ({ campaignId, campaignName, campaignCurrency = 'USD' }) => {
  const queryClient = useQueryClient();
  const [showAddPledge, setShowAddPledge] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [expandedPledge, setExpandedPledge] = useState(null);
  const [editPledge, setEditPledge] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('list');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // iter 691 — who this pledge belongs to in the shared person directory, when
  // the user picked somebody the ecosystem already holds.
  const [linkedPerson, setLinkedPerson] = useState(null);
  const fileInputRef = useRef(null);

  const [pledgeForm, setPledgeForm] = useState({
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    amount: '',
    currency: campaignCurrency,
    expected_date: '',
    notes: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    currency: campaignCurrency,
    payment_method: 'cash',
    reference: '',
    notes: ''
  });

  // Fetch supported currencies once — used to render the currency dropdown
  // so we're aligned with the FX catalogue instead of hardcoding a list.
  const { data: currenciesData } = useQuery({
    queryKey: ['fx-currencies'],
    queryFn: async () => (await api.get('/fx/currencies')).data,
    staleTime: 5 * 60 * 1000,
  });
  const supportedCurrencies = React.useMemo(() => {
    const codes = (currenciesData?.currencies || []).map((c) => (typeof c === 'string' ? c : c.code));
    const merged = Array.from(new Set([...QUICK_CURRENCIES, ...codes]));
    return merged;
  }, [currenciesData]);

  // Fetch pledges (auth token injected by the shared api client)
  const { data: pledgesData, isLoading, isError, error: pledgesError, refetch } = useQuery({
    queryKey: ['pledges', campaignId],
    queryFn: async () => {
      const res = await api.get(`/campaigns/${campaignId}/pledges`);
      return res.data;
    },
    enabled: !!campaignId,
    retry: 1,
  });

  // Create pledge mutation
  const createPledgeMutation = useMutation({
    mutationFn: async (data) => {
      const res = await api.post(`/campaigns/${campaignId}/pledges`, { ...data, campaign_id: campaignId });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Pledge created successfully!');
      queryClient.invalidateQueries(['pledges', campaignId]);
      setShowAddPledge(false);
      resetPledgeForm();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create pledge'))
  });

  // Add payment mutation
  const addPaymentMutation = useMutation({
    mutationFn: async ({ pledgeId, data }) => {
      const res = await api.post(`/pledges/${pledgeId}/payments`, data);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Payment recorded! Campaign total: $${data.campaign_raised_amount?.toLocaleString()}`);
      queryClient.invalidateQueries(['pledges', campaignId]);
      setShowAddPayment(false);
      setSelectedPledge(null);
      resetPaymentForm();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to add payment'))
  });

  // Delete pledge mutation
  const deletePledgeMutation = useMutation({
    mutationFn: async (pledgeId) => {
      const res = await api.delete(`/pledges/${pledgeId}`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Pledge deleted');
      queryClient.invalidateQueries(['pledges', campaignId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to delete pledge'))
  });

  // Edit pledge mutation
  const editPledgeMutation = useMutation({
    mutationFn: async ({ pledgeId, data }) => {
      const res = await api.put(`/campaigns/admin/${campaignId}/pledges/${pledgeId}`, data);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Pledge updated');
      queryClient.invalidateQueries(['pledges', campaignId]);
      setEditPledge(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update pledge'))
  });

  // Reverse a payment (soft-void with audit trail)
  const reversePaymentMutation = useMutation({
    mutationFn: async ({ pledgeId, paymentId, reason }) => {
      const res = await api.post(`/pledges/${pledgeId}/payments/${paymentId}/reverse`, { reason });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Payment reversed. Pledge totals updated.');
      queryClient.invalidateQueries(['pledges', campaignId]);
      queryClient.invalidateQueries(['pledges-dashboard', campaignId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Reversal failed'))
  });

  // CSV Import — sends the file to /campaigns/{id}/pledges/import
  const importCsvMutation = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/campaigns/${campaignId}/pledges/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      setImportResult(data);
      toast.success(`Imported ${data.imported} pledge(s) · ${data.failed} row(s) skipped`);
      queryClient.invalidateQueries(['pledges', campaignId]);
      queryClient.invalidateQueries(['pledges-dashboard', campaignId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Import failed')),
    onSettled: () => setImporting(false),
  });

  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    importCsvMutation.mutate(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetPledgeForm = () => {
    // The person link belongs to the pledge being written, not to the dialog:
    // leaving it set attached the *next* pledge to the previous person.
    setLinkedPerson(null);
    setPledgeForm({
      donor_name: '',
      donor_email: '',
      donor_phone: '',
      amount: '',
      currency: campaignCurrency,
      expected_date: '',
      notes: ''
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      currency: campaignCurrency,
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
  };

  const handleCreatePledge = () => {
    if (!pledgeForm.donor_name) {
      toast.error('Donor name is required');
      return;
    }
    if (!pledgeForm.amount || parseFloat(pledgeForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    createPledgeMutation.mutate({
      ...pledgeForm,
      amount: parseFloat(pledgeForm.amount),
      // Linking, not merging: their existing records are left exactly as they are.
      person_identity_key: linkedPerson?.identity_key || '',
      person_id: linkedPerson?.person_id || '',
      donor_id: linkedPerson?.relationships?.[0]?.record_id || pledgeForm.donor_id || null,
    });
  };

  const handleAddPayment = () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    // Only enforce the "not more than remaining" ceiling when the donor is
    // paying in the pledge's base currency — cross-currency payments are
    // converted server-side and could legitimately overshoot slightly.
    const sameCurrency = (paymentForm.currency || campaignCurrency).toUpperCase() ===
      (selectedPledge?.currency || campaignCurrency).toUpperCase();
    if (sameCurrency && selectedPledge && parseFloat(paymentForm.amount) > selectedPledge.remaining_amount) {
      toast.error(`Payment cannot exceed remaining amount (${selectedPledge.currency || campaignCurrency} ${selectedPledge.remaining_amount})`);
      return;
    }
    addPaymentMutation.mutate({
      pledgeId: selectedPledge.id,
      data: {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      }
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-blue-100 text-blue-800',
      fulfilled: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  const pledges = pledgesData?.pledges || [];
  const summary = pledgesData?.summary || {};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="pledges-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="border-red-200" data-testid="pledges-error">
        <CardContent className="py-12 text-center space-y-3">
          <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
          <div>
            <p className="font-medium text-slate-900">Couldn&apos;t load pledges</p>
            <p className="text-sm text-slate-500 mt-1">{getErrorMessage(pledgesError, 'Please try again.')}</p>
          </div>
          <Button onClick={() => refetch()} variant="outline" data-testid="pledges-retry">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="pledges-tabs">
          <TabsTrigger value="list" data-testid="tab-pledges-list">
            <List className="h-4 w-4 mr-1" /> Pledges
          </TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-pledges-dashboard">
            <LayoutDashboard className="h-4 w-4 mr-1" /> Dashboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <PledgesDashboard campaignId={campaignId} />
        </TabsContent>

        <TabsContent value="list" className="space-y-6 mt-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Pledged</p>
                <p className="text-2xl font-bold">${summary.total_pledged?.toLocaleString() || 0}</p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">${summary.total_paid?.toLocaleString() || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">${summary.total_pending?.toLocaleString() || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pledges</p>
                <p className="text-2xl font-bold">{summary.count || 0}</p>
                <p className="text-xs text-muted-foreground">
                  {summary.fulfilled_count || 0} fulfilled, {summary.partial_count || 0} partial
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pledges List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Pledges</CardTitle>
              <CardDescription>Track donor pledges and payments</CardDescription>
            </div>
            <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleCsvImport}
              data-testid="pledge-csv-input"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              data-testid="import-csv-btn"
            >
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Import CSV
            </Button>
            <Dialog open={showAddPledge} onOpenChange={(open) => {
              setShowAddPledge(open);
              if (!open) resetPledgeForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-pledge-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Pledge
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Pledge</DialogTitle>
                  <DialogDescription>Record a new pledge commitment</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <PersonPicker
                    selected={linkedPerson}
                    onSelect={(person) => {
                      setLinkedPerson(person);
                      setPledgeForm((f) => ({ ...f, donor_name: person.name }));
                    }}
                    onClear={() => {
                      // Unlinking also takes back the name the link filled in,
                      // so a pledge is never saved under a name the user did
                      // not choose to type.
                      setPledgeForm((f) => (
                        f.donor_name === linkedPerson?.name ? { ...f, donor_name: '' } : f));
                      setLinkedPerson(null);
                    }}
                    label="Already on record?"
                  />
                  <div className="space-y-2">
                    <Label>Donor Name *</Label>
                    <Input
                      value={pledgeForm.donor_name}
                      onChange={(e) => setPledgeForm({ ...pledgeForm, donor_name: e.target.value })}
                      placeholder="John Doe"
                      data-testid="pledge-donor-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={pledgeForm.donor_email}
                        onChange={(e) => setPledgeForm({ ...pledgeForm, donor_email: e.target.value })}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={pledgeForm.donor_phone}
                        onChange={(e) => setPledgeForm({ ...pledgeForm, donor_phone: e.target.value })}
                        placeholder="+1234567890"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Pledge Amount *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={pledgeForm.amount}
                        onChange={(e) => setPledgeForm({ ...pledgeForm, amount: e.target.value })}
                        placeholder="1000"
                        data-testid="pledge-amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Currency *</Label>
                      <CurrencySelect
                        value={pledgeForm.currency}
                        onChange={(v) => setPledgeForm({ ...pledgeForm, currency: v })}
                        homeCurrency={campaignCurrency}
                        testId="pledge-currency"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Expected Payment Date</Label>
                      <Input
                        type="date"
                        value={pledgeForm.expected_date}
                        onChange={(e) => setPledgeForm({ ...pledgeForm, expected_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={pledgeForm.notes}
                      onChange={(e) => setPledgeForm({ ...pledgeForm, notes: e.target.value })}
                      placeholder="Any additional notes..."
                      rows={2}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" data-testid="pledge-cancel-btn"
                          onClick={() => { setShowAddPledge(false); resetPledgeForm(); }}>Cancel</Button>
                  <Button 
                    onClick={handleCreatePledge}
                    disabled={createPledgeMutation.isPending}
                    data-testid="submit-pledge-btn"
                  >
                    {createPledgeMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Pledge
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {pledges.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No pledges yet</p>
              <p className="text-sm">Add a pledge to start tracking donations</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pledges.map((pledge) => (
                <div 
                  key={pledge.id} 
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{pledge.donor_name}</h4>
                        {getStatusBadge(pledge.status)}
                        <Badge variant="outline" className="text-xs" data-testid={`pledge-currency-${pledge.id}`}>
                          {pledge.currency || campaignCurrency}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        {pledge.donor_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {pledge.donor_email}
                          </span>
                        )}
                        {pledge.donor_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {pledge.donor_phone}
                          </span>
                        )}
                        {pledge.expected_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> Due: {pledge.expected_date}
                          </span>
                        )}
                      </div>
                      
                      {/* Progress bar */}
                      <div className="mt-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Paid: {pledge.currency || campaignCurrency} {pledge.paid_amount?.toLocaleString()}</span>
                          <span>of {pledge.currency || campaignCurrency} {pledge.amount?.toLocaleString()}</span>
                        </div>
                        <Progress 
                          value={(pledge.paid_amount / pledge.amount) * 100} 
                          className="h-2"
                        />
                        {pledge.remaining_amount > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {pledge.currency || campaignCurrency} {pledge.remaining_amount?.toLocaleString()} remaining
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditPledge(pledge);
                          setEditForm({
                            donor_name: pledge.donor_name || '',
                            donor_email: pledge.donor_email || '',
                            donor_phone: pledge.donor_phone || '',
                            whatsapp_number: pledge.whatsapp_number || '',
                            amount: pledge.amount || 0,
                            paid_amount: pledge.paid_amount || 0,
                            frequency: pledge.frequency || 'one_time',
                            message: pledge.message || '',
                            donor_visibility: pledge.donor_visibility || 'anonymous',
                            status: pledge.status || 'pending',
                          });
                        }}
                        data-testid={`edit-pledge-${pledge.id}`}
                      >
                        <Edit2 className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      {pledge.status !== 'fulfilled' && pledge.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedPledge(pledge);
                            setPaymentForm({
                              ...paymentForm,
                              amount: pledge.remaining_amount.toString(),
                              currency: pledge.currency || campaignCurrency,
                            });
                            setShowAddPayment(true);
                          }}
                          data-testid={`record-payment-${pledge.id}`}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Record Payment
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedPledge(expandedPledge === pledge.id ? null : pledge.id)}
                      >
                        {expandedPledge === pledge.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this pledge?')) {
                            deletePledgeMutation.mutate(pledge.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Payment History */}
                  {expandedPledge === pledge.id && pledge.payments?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h5 className="text-sm font-medium mb-2">Payment History</h5>
                      <div className="space-y-2">
                        {pledge.payments.map((payment, idx) => {
                          const donorCcy = payment.currency || pledge.currency || campaignCurrency;
                          const baseCcy = payment.base_currency || pledge.currency || campaignCurrency;
                          const isCross = donorCcy !== baseCcy && payment.base_amount != null;
                          const isReversed = !!payment.reversed_at;
                          return (
                            <div
                              key={payment.id || idx}
                              className={`flex items-center justify-between text-sm p-2 rounded ${isReversed ? 'bg-red-50 border border-red-200' : 'bg-muted/50'}`}
                              data-testid={`payment-row-${payment.id || idx}`}
                            >
                              <div className="flex items-center gap-3 flex-wrap">
                                <Receipt className={`h-4 w-4 ${isReversed ? 'text-red-500' : 'text-green-500'}`} />
                                <span className={`font-medium ${isReversed ? 'line-through text-slate-400' : ''}`}>
                                  {donorCcy} {payment.amount?.toLocaleString()}
                                </span>
                                {isCross && (
                                  <span className="text-xs text-blue-700 bg-blue-50 rounded px-1.5 py-0.5">
                                    ≈ {baseCcy} {Number(payment.base_amount).toLocaleString()} @ {Number(payment.fx_rate || 0).toFixed(4)}
                                  </span>
                                )}
                                <Badge variant="outline" className="text-xs">{payment.payment_method}</Badge>
                                {payment.reference && (
                                  <span className="text-muted-foreground text-xs">Ref: {payment.reference}</span>
                                )}
                                {isReversed && (
                                  <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Reversed</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-xs">
                                  {new Date(payment.recorded_at).toLocaleDateString()}
                                </span>
                                {!isReversed && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    onClick={() => {
                                      const reason = window.prompt('Reason for reversing this payment?');
                                      if (reason === null) return;
                                      reversePaymentMutation.mutate({
                                        pledgeId: pledge.id,
                                        paymentId: payment.id,
                                        reason,
                                      });
                                    }}
                                    data-testid={`reverse-payment-${payment.id}`}
                                  >
                                    <Undo2 className="h-3 w-3 mr-1" /> Reverse
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Payment Dialog */}
      <Dialog open={showAddPayment} onOpenChange={(open) => {
        setShowAddPayment(open);
        if (!open) {
          setSelectedPledge(null);
          resetPaymentForm();
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {selectedPledge && (
                <>
                  Recording payment for {selectedPledge.donor_name}&apos;s pledge of {selectedPledge.currency || campaignCurrency} {selectedPledge.amount?.toLocaleString()}
                  <br />
                  <span className="text-orange-600">Remaining: {selectedPledge.currency || campaignCurrency} {selectedPledge.remaining_amount?.toLocaleString()}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label>Payment Amount *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="500"
                  data-testid="payment-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency *</Label>
                <CurrencySelect
                  value={paymentForm.currency}
                  onChange={(v) => setPaymentForm({ ...paymentForm, currency: v })}
                  homeCurrency={selectedPledge?.currency || campaignCurrency}
                  testId="payment-currency"
                />
              </div>
            </div>
            {selectedPledge && paymentForm.currency && (paymentForm.currency !== (selectedPledge.currency || campaignCurrency)) && (
              <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-md p-2">
                💱 This payment will be auto-converted from {paymentForm.currency} to {selectedPledge.currency || campaignCurrency} using current FX rate.
                The receipt will show both amounts.
              </div>
            )}
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference/Transaction ID</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                placeholder="TXN-12345"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Payment notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPayment(false)}>Cancel</Button>
            <Button 
              onClick={handleAddPayment}
              disabled={addPaymentMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-payment-btn"
            >
              {addPaymentMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Pledge Dialog */}
      <Dialog open={!!editPledge} onOpenChange={(open) => { if (!open) setEditPledge(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Pledge</DialogTitle>
            <DialogDescription>Update donor information, amounts, and visibility</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Donor Name</Label><Input value={editForm.donor_name} onChange={e => setEditForm({...editForm, donor_name: e.target.value})} data-testid="edit-donor-name" /></div>
              <div><Label>Email</Label><Input type="email" value={editForm.donor_email} onChange={e => setEditForm({...editForm, donor_email: e.target.value})} data-testid="edit-donor-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={editForm.donor_phone} onChange={e => setEditForm({...editForm, donor_phone: e.target.value})} data-testid="edit-donor-phone" /></div>
              <div><Label>WhatsApp</Label><Input value={editForm.whatsapp_number} onChange={e => setEditForm({...editForm, whatsapp_number: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Pledge Amount ($)</Label><Input type="number" value={editForm.amount} onChange={e => setEditForm({...editForm, amount: parseFloat(e.target.value) || 0})} data-testid="edit-pledge-amount" /></div>
              <div><Label>Paid Amount ($)</Label><Input type="number" value={editForm.paid_amount} onChange={e => setEditForm({...editForm, paid_amount: parseFloat(e.target.value) || 0})} data-testid="edit-paid-amount" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={editForm.frequency} onValueChange={v => setEditForm({...editForm, frequency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One Time</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm({...editForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="fulfilled">Fulfilled</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Public Visibility</Label>
              <Select value={editForm.donor_visibility} onValueChange={v => setEditForm({...editForm, donor_visibility: v})}>
                <SelectTrigger data-testid="edit-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public (full name)</SelectItem>
                  <SelectItem value="partial">Partial (first name + initial)</SelectItem>
                  <SelectItem value="anonymous">Anonymous</SelectItem>
                  <SelectItem value="private">Private (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Message</Label><Textarea value={editForm.message} onChange={e => setEditForm({...editForm, message: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPledge(null)}>Cancel</Button>
            <Button onClick={() => editPledgeMutation.mutate({ pledgeId: editPledge.id, data: editForm })} disabled={editPledgeMutation.isPending} data-testid="save-edit-pledge">
              {editPledgeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PledgesManagement;
